import type {
  Facility,
  NumberRecord,
  Email,
  DocumentClassification,
  SystemPrompt,
  DataStructure,
  Organization,
  User,
  GlobalSettings,
  Category,
  Capability,
  AuditLog,
  Integration,
  ExtractionStructure,
  DocumentRule,
  AutomaticAction,
  ManualAction,
  MessageTemplate,
} from "./types"

export const mockOrganizations: Organization[] = [
  {
    id: "1",
    name: "Healthcare Network Inc.",
    created_at: "2024-01-01T10:00:00Z",
    updated_at: "2024-01-01T10:00:00Z",
  },
  {
    id: "2",
    name: "Regional Medical Group",
    created_at: "2024-01-05T10:00:00Z",
    updated_at: "2024-01-05T10:00:00Z",
  },
]

export const mockNumbers: NumberRecord[] = [
  {
    id: "1",
    number: "+1 (555) 123-4567",
    provider_id: "prov_001",
    provider: "Twilio",
    facility_ids: ["1"],
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
  {
    id: "2",
    number: "+1 (555) 234-5678",
    provider_id: "prov_002",
    provider: "Twilio",
    facility_ids: ["1"],
    created_at: "2024-01-16T10:00:00Z",
    updated_at: "2024-01-16T10:00:00Z",
  },
  {
    id: "3",
    number: "+1 (555) 345-6789",
    provider_id: "prov_003",
    provider: "Bandwidth",
    facility_ids: ["2"],
    created_at: "2024-01-17T10:00:00Z",
    updated_at: "2024-01-17T10:00:00Z",
  },
]

export const mockEmails: Email[] = [
  {
    id: "1",
    email: "fax@clinic-main.com",
    facility_ids: ["1"],
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
  {
    id: "2",
    email: "records@clinic-main.com",
    facility_ids: ["1"],
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
  {
    id: "3",
    email: "fax@clinic-north.com",
    facility_ids: ["2"],
    created_at: "2024-01-16T10:00:00Z",
    updated_at: "2024-01-16T10:00:00Z",
  },
  {
    id: "4",
    email: "referrals@healthcare-network.com",
    is_group: true,
    group_name: "Referrals Team",
    facility_ids: ["1", "2"],
    created_at: "2024-01-17T10:00:00Z",
    updated_at: "2024-01-17T10:00:00Z",
  },
  {
    id: "5",
    email: "urgent@healthcare-network.com",
    is_group: true,
    group_name: "Urgent Care Group",
    facility_ids: ["1", "2"],
    created_at: "2024-01-18T10:00:00Z",
    updated_at: "2024-01-18T10:00:00Z",
  },
]

export const mockUsers: User[] = [
  {
    id: "1",
    name: "Dr. Sarah Johnson",
    email: "sarah.johnson@clinic.com",
    role: "admin",
    organization_id: 1,
    facility_ids: ["1", "2"],
    created_at: "2024-01-10T10:00:00Z",
    updated_at: "2024-01-10T10:00:00Z",
  },
  {
    id: "2",
    name: "John Smith",
    email: "john.smith@clinic.com",
    role: "user",
    organization_id: 1,
    facility_ids: ["1"],
    created_at: "2024-01-12T10:00:00Z",
    updated_at: "2024-01-12T10:00:00Z",
  },
]

export const mockCapabilities: Capability[] = [
  {
    id: "1",
    name: "ICU",
    description: "Intensive Care Unit capabilities",
    facility_ids: [1],
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
  {
    id: "2",
    name: "Dialysis Center",
    description: "Dialysis treatment capabilities",
    facility_ids: [2],
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
]

export const mockCategories: Category[] = [
  {
    id: "1",
    name: "Urgent Care",
    description: "High priority cases requiring immediate attention",
    rules: ["Contains 'urgent' or 'stat'", "Response time < 2 hours"],
    facility_ids: [1],
    category_number_id: 1,
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
  {
    id: "2",
    name: "Routine",
    description: "Standard processing for non-urgent cases",
    rules: ["Standard processing", "Response time < 24 hours"],
    facility_ids: [1, 2],
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
]

export const mockFacilities: Facility[] = [
  {
    id: "1",
    name: "Main Clinic",
    organization_id: 1,
    number_ids: ["1", "2"],
    email_ids: ["1", "2"],
    user_ids: ["1", "2"],
    numbers: [mockNumbers[0], mockNumbers[1]],
    emails: [mockEmails[0], mockEmails[1]],
    users: [mockUsers[0], mockUsers[1]],
    category_ids: [1, 2],
    capability_ids: [1],
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
  {
    id: "2",
    name: "North Branch",
    organization_id: 1,
    number_ids: ["3"],
    email_ids: ["3"],
    user_ids: ["1"],
    numbers: [mockNumbers[2]],
    emails: [mockEmails[2]],
    users: [mockUsers[0]],
    category_ids: [2],
    capability_ids: [2],
    created_at: "2024-01-16T10:00:00Z",
    updated_at: "2024-01-16T10:00:00Z",
  },
]

export const mockClassifications: DocumentClassification[] = [
  {
    id: "1",
    name: "Referral",
    criteria:
      "Documents containing referral requests, consultation requests, or patient transfers from other healthcare providers",
    description: "Patient referral documents from other providers",
    extraction_prompt:
      "Extract patient name, date of birth, referring physician, reason for referral, urgency level, and contact information",
    show_in_sidebar: true,
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
  {
    id: "2",
    name: "Lab Results",
    criteria:
      "Laboratory test results, pathology reports, diagnostic test results, or any documents containing test values and reference ranges",
    description: "Laboratory test results and reports",
    extraction_prompt:
      "Extract patient name, test date, test names, test values, units, reference ranges, and abnormal flags",
    show_in_sidebar: true,
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
  {
    id: "3",
    name: "Medication",
    criteria:
      "Prescription documents, medication orders, pharmacy communications, or documents related to patient medications",
    description: "Prescription and medication-related documents",
    extraction_prompt:
      "Extract patient name, medication name, dosage, frequency, prescribing physician, and pharmacy information",
    show_in_sidebar: true,
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
]

export const mockPrompts: SystemPrompt[] = [
  {
    id: "1",
    name: "Referral Extraction",
    prompt:
      "Extract patient information, referring provider, reason for referral, and urgency level from the referral document.",
    document_type: "referral",
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
  {
    id: "2",
    name: "Lab Results Summary",
    prompt: "Summarize the lab test results, highlighting any abnormal values and critical findings.",
    document_type: "lab results",
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
]

export const mockDataStructures: DataStructure[] = [
  {
    id: "1",
    name: "Referral Schema",
    schema: {
      type: "object",
      properties: {
        patient_name: { type: "string" },
        patient_dob: { type: "string", format: "date" },
        referring_provider: { type: "string" },
        reason: { type: "string" },
        urgency: { type: "string", enum: ["routine", "urgent", "stat"] },
      },
      required: ["patient_name", "referring_provider", "reason"],
    },
    document_type: "referral",
    prompt_id: 1,
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
  {
    id: "2",
    name: "Lab Results Schema",
    schema: {
      type: "object",
      properties: {
        patient_name: { type: "string" },
        test_date: { type: "string", format: "date" },
        tests: {
          type: "array",
          items: {
            type: "object",
            properties: {
              name: { type: "string" },
              value: { type: "string" },
              unit: { type: "string" },
              reference_range: { type: "string" },
              abnormal: { type: "boolean" },
            },
          },
        },
      },
      required: ["patient_name", "test_date", "tests"],
    },
    document_type: "lab results",
    prompt_id: 2,
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
]

export const mockGlobalSettings: GlobalSettings = {
  id: "1",
  billing_contact_email: "billing@clinic.com",
  notification_emails: ["admin@clinic.com", "alerts@clinic.com"],
  created_at: "2024-01-01T10:00:00Z",
  updated_at: "2024-01-01T10:00:00Z",
}

export const mockIntegrations: Integration[] = [
  {
    id: "1",
    name: "Epic EHR",
    enabled: true,
    settings: {
      api_endpoint: "https://api.epic.com",
      sync_interval: 300,
    },
    created_at: "2024-01-10T10:00:00Z",
    updated_at: "2024-01-10T10:00:00Z",
  },
  {
    id: "2",
    name: "Slack Notifications",
    enabled: false,
    settings: {
      webhook_url: "https://hooks.slack.com/...",
    },
    created_at: "2024-01-12T10:00:00Z",
    updated_at: "2024-01-12T10:00:00Z",
  },
]

export const mockAuditLogs: AuditLog[] = [
  {
    id: "1",
    user_id: 1,
    action: "created",
    entity_type: "facility",
    entity_id: "1",
    changes: { name: "Main Clinic" },
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
  {
    id: "2",
    user_id: 1,
    action: "updated",
    entity_type: "user",
    entity_id: "2",
    changes: { role: "admin" },
    created_at: "2024-01-16T11:30:00Z",
    updated_at: "2024-01-16T11:30:00Z",
  },
]

export const mockExtractionStructures: ExtractionStructure[] = [
  {
    id: "1",
    name: "Referral Fields",
    fields: [
      { name: "patient_name", type: "string", required: true, description: "Full name of the patient" },
      { name: "patient_dob", type: "date", required: true, description: "Date of birth" },
      { name: "referring_provider", type: "string", required: true, description: "Name of referring physician" },
      { name: "reason", type: "text", required: true, description: "Reason for referral" },
      { name: "urgency", type: "select", required: false, description: "Urgency level" },
    ],
    document_classification_id: "1",
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
]

export const mockDocumentRules: DocumentRule[] = [
  {
    id: "1",
    name: "Urgent Referral Alert",
    trigger: "received",
    actions: [
      {
        type: "send_notification",
        config: { recipients: ["admin@clinic.com"], message: "Urgent referral received" },
      },
      {
        type: "assign_priority",
        config: { priority: "high" },
      },
    ],
    document_classification_id: "1",
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
]

export const mockAutomaticActions: AutomaticAction[] = [
  {
    id: "1",
    name: "Notify on Urgent Referral",
    document_classification_id: "1",
    trigger: "on_receive",
    action_type: "email",
    config: {
      template_id: "1",
    },
    enabled: true,
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
  {
    id: "2",
    name: "Webhook on Lab Results",
    document_classification_id: "2",
    trigger: "on_save",
    action_type: "webhook",
    config: {
      webhook_url: "https://api.example.com/lab-results",
    },
    enabled: true,
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
]

export const mockManualActions: ManualAction[] = [
  {
    id: "1",
    name: "Send to Patient Portal",
    document_classification_id: "2",
    action_type: "integration",
    config: {
      integration_id: "1",
    },
    enabled: true,
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
  {
    id: "2",
    name: "SMS Patient Notification",
    document_classification_id: "1",
    action_type: "sms",
    config: {
      template_id: "2",
    },
    enabled: true,
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
]

export const mockMessageTemplates: MessageTemplate[] = [
  {
    id: "1",
    name: "Urgent Referral Notification",
    type: "email",
    subject: "Urgent Referral Received - {{patient_name}}",
    body: "An urgent referral has been received for patient {{patient_name}}. Referring provider: {{referring_provider}}. Reason: {{reason}}.",
    variables: ["patient_name", "referring_provider", "reason"],
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
  {
    id: "2",
    name: "Referral Confirmation SMS",
    type: "sms",
    body: "Your referral for {{patient_name}} has been received and is being processed. Reference: {{reference_number}}",
    variables: ["patient_name", "reference_number"],
    created_at: "2024-01-15T10:00:00Z",
    updated_at: "2024-01-15T10:00:00Z",
  },
  {
    id: "3",
    name: "Lab Results Available",
    type: "email",
    subject: "Lab Results Available - {{patient_name}}",
    body: "Lab results for {{patient_name}} are now available. Test date: {{test_date}}. Please review in the portal.",
    variables: ["patient_name", "test_date"],
    created_at: "2024-01-16T10:00:00Z",
    updated_at: "2024-01-16T10:00:00Z",
  },
]
